<script setup>
/* global axios */
// Aba "Conexão" de um inbox de provedor (Evolution/WAHA/UazAPI): status da
// instância, QR code para parear e ações (reconectar/desconectar/reiniciar).
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import QRCode from 'qrcode';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { useCallMethodExclusion } from 'dashboard/composables/useCallMethodExclusion';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban/connections`;

// Exclusão: AstraCall não liga se Wavoip/oficial já ativos nesta caixa.
const { fetchMethods, conflictLabelsFor } = useCallMethodExclusion();
const callConflicts = computed(() => conflictLabelsFor('astracall'));
const callsBlocked = computed(() => callConflicts.value.length > 0);

const instance = ref(null);
const loading = ref(true);
const qrImage = ref('');
const qrStatus = ref('');
const qrError = ref('');
const showQR = ref(false);
let qrTimer = null;
let stateTimer = null;

const STATUS_CLASS = {
  connected: 'bg-n-teal-3 text-n-teal-11',
  connecting: 'bg-n-amber-3 text-n-amber-11',
  disconnected: 'bg-n-alpha-2 text-n-slate-11',
};
const statusLabel = computed(() =>
  t(
    `CONNECTIONS.status.${instance.value?.status}`,
    instance.value?.status || ''
  )
);
const isConnected = computed(() => instance.value?.status === 'connected');
const isAstracall = computed(() => instance.value?.provider === 'astracall');
const isUazapi = computed(() => instance.value?.provider === 'uazapi');
const isWaha = computed(() => instance.value?.provider === 'waha');
const isEvolutionGo = computed(
  () => instance.value?.provider === 'evolution-go'
);

// Ligações (só AstraCall): habilita o botão de telefone no header da conversa.
const callsEnabled = ref(false);
const savingCalls = ref(false);
const toggleCalls = async val => {
  const prev = callsEnabled.value;
  callsEnabled.value = val;
  savingCalls.value = true;
  try {
    await axios.patch(`${base}/${instance.value.instance_name}/calls_enabled`, {
      enabled: val,
    });
    useAlert(t('CONNECTIONS.calls.saved'));
  } catch (e) {
    callsEnabled.value = prev;
    useAlert(t('CONNECTIONS.config.saveError'));
  } finally {
    savingCalls.value = false;
  }
};

// "Ignorar chamadas recebidas": ao chegar ligação, recusa na hora + (opcional)
// envia a mensagem configurada. NÃO afeta originar ligação.
const rejectCalls = ref(false);
const rejectMessage = ref('');
const savingReject = ref(false);
const saveReject = async (val, msg) => {
  savingReject.value = true;
  try {
    await axios.patch(`${base}/${instance.value.instance_name}/reject_calls`, {
      enabled: val,
      message: msg,
    });
    useAlert(t('CONNECTIONS.rejectCalls.saved'));
  } catch (e) {
    useAlert(t('CONNECTIONS.config.saveError'));
  } finally {
    savingReject.value = false;
  }
};
const toggleReject = val => {
  rejectCalls.value = val;
  saveReject(val, rejectMessage.value);
};
const saveRejectMessage = () => {
  if (rejectCalls.value) saveReject(true, rejectMessage.value);
};

// Proxy de saída (só AstraCall): a conexão do WhatsApp da sessão passa a sair por
// um proxy http/https/socks5. Campos separados que compõem a URL enviada à API.
const proxy = reactive({ scheme: 'socks5', host: '', port: '', user: '', pass: '' });
const proxyEnabled = ref(false);
const savingProxy = ref(false);

const resetProxyFields = () => {
  proxy.scheme = 'socks5';
  proxy.host = '';
  proxy.port = '';
  proxy.user = '';
  proxy.pass = '';
};
const parseProxy = url => {
  resetProxyFields();
  if (!url) return;
  try {
    const u = new URL(url);
    proxy.scheme = u.protocol.replace(':', '') || 'socks5';
    proxy.host = u.hostname;
    proxy.port = u.port;
    proxy.user = decodeURIComponent(u.username || '');
    proxy.pass = decodeURIComponent(u.password || '');
  } catch (e) {
    /* URL inválida — mantém campos vazios */
  }
};
const buildProxyUrl = () => {
  if (!proxy.host.trim()) return '';
  const auth = proxy.user
    ? `${encodeURIComponent(proxy.user)}:${encodeURIComponent(proxy.pass)}@`
    : '';
  const port = proxy.port ? `:${String(proxy.port).trim()}` : '';
  return `${proxy.scheme}://${auth}${proxy.host.trim()}${port}`;
};
const loadProxy = async () => {
  if (!isAstracall.value && !isUazapi.value && !isEvolutionGo.value) return;
  try {
    const { data } = await axios.get(`${base}/proxy`, {
      params: { inbox_id: props.inbox.id },
    });
    proxyEnabled.value = data.data?.enabled === true;
    parseProxy(data.data?.proxy);
  } catch (e) {
    /* proxy indisponível */
  }
};
const saveProxy = async () => {
  savingProxy.value = true;
  try {
    const { data } = await axios.put(`${base}/proxy`, {
      inbox_id: props.inbox.id,
      proxy: buildProxyUrl(),
    });
    proxyEnabled.value = data.data?.enabled === true;
    useAlert(t('CONNECTIONS.proxy.saved'));
  } catch (e) {
    useAlert(e?.response?.data?.error || t('CONNECTIONS.config.saveError'));
  } finally {
    savingProxy.value = false;
  }
};
const clearProxy = async () => {
  resetProxyFields();
  await saveProxy();
};

// Gestão da conexão (ignorar grupos, gravação, etc.). Evolution e AstraCall
// expõem conjuntos diferentes de ajustes.
const config = ref(null);
const savingKey = ref('');
const EVOLUTION_TOGGLES = [
  'agentName',
  'ignoreGroups',
  'rejectCalls',
  'alwaysOnline',
  'readMessages',
  'readStatus',
];
const ASTRACALL_TOGGLES = [
  'agentName',
  'ignoreGroups',
  'alwaysOnline',
  'readMessages',
  'syncChannels',
  'recording',
];
// UazAPI (uazapiGO) só suporta estes dois toggles; o delay anti-ban tem UI própria.
const UAZAPI_TOGGLES = ['ignoreGroups', 'alwaysOnline'];
// WAHA: ajustes nativos do servidor (config.ignore + calls/chatwoot apps + markOnline).
const WAHA_TOGGLES = [
  'agentName',
  'rejectCalls',
  'ignoreGroups',
  'ignoreChannels',
  'ignoreBroadcast',
  'alwaysOnline',
  'linkPreview',
];
const TOGGLES = computed(() => {
  if (isUazapi.value) return UAZAPI_TOGGLES;
  if (isWaha.value) return WAHA_TOGGLES;
  return isAstracall.value ? ASTRACALL_TOGGLES : EVOLUTION_TOGGLES;
});

const loadConfig = async () => {
  if (!instance.value) return;
  try {
    const { data } = await axios.get(
      `${base}/${instance.value.instance_name}/config`
    );
    config.value = data.data || null;
  } catch (e) {
    config.value = null;
  }
};
// AstraCall: os ajustes valem mesmo desconectado (recording/chatwoot respondem
// sempre). Evolution: só quando conectado.
const loadConfigIfConnected = () => {
  if (
    (isConnected.value ||
      isAstracall.value ||
      isUazapi.value ||
      isWaha.value) &&
    !config.value
  )
    loadConfig();
};
const saveConfig = async (key, value) => {
  const prev = config.value?.[key];
  config.value = { ...config.value, [key]: value };
  savingKey.value = key;
  try {
    const { data } = await axios.patch(
      `${base}/${instance.value.instance_name}/config`,
      { [key]: value }
    );
    // O backend RE-LÊ a config no provedor e devolve o estado REAL. Antes a gente
    // ignorava a resposta e sempre dizia "salvo" — se o provedor não persistisse, o
    // toggle só voltava atrás no refresh (parecia que tinha salvo). Agora manda a
    // verdade na hora: o toggle reflete o provedor e avisa se não pegou.
    if (data?.data) config.value = data.data;
    const applied = data?.data?.[key];
    // eslint-disable-next-line eqeqeq
    const ok = applied === undefined || applied == value || !!applied === !!value;
    useAlert(
      ok ? t('CONNECTIONS.config.saved') : t('CONNECTIONS.config.notPersisted')
    );
  } catch (e) {
    config.value = { ...config.value, [key]: prev };
    useAlert(t('CONNECTIONS.config.saveError'));
  } finally {
    savingKey.value = '';
  }
};

const load = async () => {
  const { data } = await axios.get(`${base}/by_inbox`, {
    params: { inbox_id: props.inbox.id },
  });
  instance.value = data.data;
  callsEnabled.value = data.data?.meta?.calls_enabled === true;
  rejectCalls.value = data.data?.meta?.reject_incoming_calls === true;
  rejectMessage.value = data.data?.meta?.reject_incoming_message || '';
  loading.value = false;
};

// Consulta o estado real no provedor e atualiza o badge (connecting → connected).
const refreshState = async () => {
  if (!instance.value) return;
  try {
    const { data } = await axios.get(
      `${base}/${instance.value.instance_name}/state`
    );
    if (data.status && instance.value) {
      instance.value = { ...instance.value, status: data.status };
      loadConfigIfConnected();
    }
  } catch (e) {
    // estado indisponível — mantém o atual
  }
};
const stopState = () => {
  if (stateTimer) clearInterval(stateTimer);
  stateTimer = null;
};
const startStatePolling = async () => {
  await refreshState();
  stateTimer = setInterval(refreshState, 5000);
};

// O QR pode vir como imagem (data:/base64, ex. Evolution/WAHA) ou como STRING de
// conteúdo (ex. AstraCall entrega o texto do QR) — nesse caso rendemos no cliente.
const normalizeQr = async raw => {
  if (!raw) return '';
  if (raw.startsWith('data:')) return raw;
  // conteúdo de QR (URL/deep-link/jid) → gera a imagem localmente
  if (/[:@]|wa\.me|linked_devices/.test(raw)) {
    try {
      return await QRCode.toDataURL(raw, { margin: 1, width: 256 });
    } catch (e) {
      return '';
    }
  }
  // base64 puro de imagem
  return `data:image/png;base64,${raw}`;
};
const stopQR = () => {
  if (qrTimer) clearInterval(qrTimer);
  qrTimer = null;
};
const pollQR = async () => {
  if (!instance.value) return;
  try {
    const { data } = await axios.get(
      `${base}/${instance.value.instance_name}/qrcode`
    );
    if (data.error) qrError.value = data.error;
    qrImage.value = await normalizeQr(data.qrcode);
    qrStatus.value = data.status?.state || data.status || '';
    if (/(open|connected)/i.test(qrStatus.value)) {
      stopQR();
      showQR.value = false;
      await load();
      loadConfigIfConnected();
    }
  } catch (e) {
    qrError.value = e?.response?.data?.error || t('CONNECTIONS.qrLoading');
  }
};
const connect = async () => {
  qrImage.value = '';
  qrError.value = '';
  qrStatus.value = '';
  showQR.value = true;
  await pollQR();
  qrTimer = setInterval(pollQR, 4000);
};
const act = async action => {
  await axios.post(`${base}/${instance.value.instance_name}/${action}`);
  await load();
};

// Recria a conexão: remove a sessão no AstraCall e cria outra p/ a mesma caixa.
// Serve p/ destravar quando a sessão bugou no provedor (logout/restart não bastam).
const recreating = ref(false);
const recreate = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('CONNECTIONS.recreate.confirm'))) return;
  recreating.value = true;
  try {
    await axios.post(`${base}/${instance.value.instance_name}/recreate`);
    await load();
    useAlert(t('CONNECTIONS.recreate.done'));
    await connect();
  } catch (e) {
    useAlert(e?.response?.data?.error || t('CONNECTIONS.recreate.error'));
  } finally {
    recreating.value = false;
  }
};

// Importar histórico (só AstraCall): traz conversas/mensagens já existentes no
// aparelho para dentro do Chatwoot. Roda em background; o front faz polling.
const importState = ref({});
const importStarting = ref(false);
const importMode = ref('auto'); // 'auto' = desde a última msg | 'days' = janela em dias
const importDays = ref(30); // dias digitados (0 = tudo)
let importTimer = null;
const importRunning = computed(() =>
  ['queued', 'running'].includes(importState.value?.status)
);
const importPct = computed(() => {
  const total = Number(importState.value?.total_chats) || 0;
  const done = Number(importState.value?.done_chats) || 0;
  if (!total) return importRunning.value ? 5 : 0;
  return Math.min(100, Math.round((done / total) * 100));
});
const stopImportPoll = () => {
  if (importTimer) clearInterval(importTimer);
  importTimer = null;
};
const pollImportStatus = async () => {
  if (!instance.value) return;
  try {
    const { data } = await axios.get(
      `${base}/${instance.value.instance_name}/import_status`
    );
    importState.value = data.import || {};
    contactsImport.value = data.import_contacts || {};
    if (!importRunning.value && !contactsImportRunning.value) stopImportPoll();
  } catch (e) {
    stopImportPoll();
  }
};
const startImport = async () => {
  if (importStarting.value || importRunning.value) return;
  importStarting.value = true;
  try {
    const payload =
      importMode.value === 'auto'
        ? { since_last: true }
        : { days: Math.max(0, Number(importDays.value) || 0) };
    await axios.post(
      `${base}/${instance.value.instance_name}/import_history`,
      payload
    );
    importState.value = { status: 'queued' };
    stopImportPoll();
    importTimer = setInterval(pollImportStatus, 2000);
  } catch (e) {
    useAlert(e?.response?.data?.error || t('CONNECTIONS.import.error'));
  } finally {
    importStarting.value = false;
  }
};

// Importar contatos (agenda sincronizada na conexão) — opt-in, roda em background.
const contactsImport = ref({});
const contactsImportStarting = ref(false);
const contactsImportRunning = computed(() =>
  ['queued', 'running'].includes(contactsImport.value?.status)
);
const startContactsImport = async () => {
  if (contactsImportStarting.value || contactsImportRunning.value) return;
  contactsImportStarting.value = true;
  try {
    await axios.post(
      `${base}/${instance.value.instance_name}/import_contacts`
    );
    contactsImport.value = { status: 'queued' };
    stopImportPoll();
    importTimer = setInterval(pollImportStatus, 2000);
  } catch (e) {
    useAlert(e?.response?.data?.error || t('CONNECTIONS.importContacts.error'));
  } finally {
    contactsImportStarting.value = false;
  }
};

onMounted(async () => {
  fetchMethods(accountId, props.inbox.id);
  await load();
  if (instance.value) {
    startStatePolling();
    loadConfigIfConnected();
    loadProxy();
    importState.value = instance.value.meta?.import || {};
    contactsImport.value = instance.value.meta?.import_contacts || {};
    if (importRunning.value || contactsImportRunning.value) {
      importTimer = setInterval(pollImportStatus, 2000);
    }
  }
});
onBeforeUnmount(() => {
  stopQR();
  stopState();
  stopImportPoll();
});
</script>

<template>
  <div v-if="!loading && instance" class="py-4">
    <div
      class="flex items-center gap-3 p-4 mb-4 border rounded-xl border-n-weak bg-n-solid-1"
    >
      <div class="min-w-0">
        <div class="font-medium truncate text-n-slate-12">
          {{ instance.provider_name || instance.provider }}
        </div>
        <div class="text-xs text-n-slate-10">{{ instance.instance_name }}</div>
      </div>
      <span
        class="px-2 py-0.5 text-[0.6875rem] font-medium rounded-full"
        :class="STATUS_CLASS[instance.status] || 'bg-n-alpha-2 text-n-slate-11'"
        >{{ statusLabel }}</span
      >
      <div class="flex items-center gap-2 ml-auto">
        <NextButton
          v-if="!isConnected"
          sm
          blue
          :label="t('CONNECTIONS.act.qr')"
          @click="connect"
        />
        <NextButton
          v-else
          sm
          slate
          faded
          :label="t('CONNECTIONS.act.logout')"
          @click="act('logout')"
        />
        <NextButton
          sm
          slate
          faded
          :label="t('CONNECTIONS.act.restart')"
          @click="act('restart')"
        />
        <NextButton
          v-if="isAstracall"
          sm
          ruby
          faded
          :label="t('CONNECTIONS.act.recreate')"
          :is-loading="recreating"
          :disabled="recreating"
          @click="recreate"
        />
      </div>
    </div>

    <div v-if="showQR" class="p-6 text-center border rounded-xl border-n-weak">
      <p class="mb-3 text-sm text-n-slate-11">{{ t('CONNECTIONS.qrHint') }}</p>
      <img
        v-if="qrImage"
        :src="qrImage"
        alt="QR"
        class="mx-auto border rounded-lg w-60 h-60 border-n-weak"
      />
      <div
        v-else
        class="flex items-center justify-center mx-auto border rounded-lg w-60 h-60 border-n-weak text-n-slate-10"
      >
        {{ qrError || t('CONNECTIONS.qrLoading') }}
      </div>
      <p v-if="qrStatus" class="mt-2 text-xs text-n-slate-10">{{ qrStatus }}</p>
    </div>

    <!-- Ligações (AstraCall): habilita o botão de telefone na conversa -->
    <div
      v-if="isAstracall"
      class="flex items-center gap-3 p-4 mb-4 border rounded-xl border-n-weak bg-n-solid-1"
    >
      <div
        class="flex items-center justify-center rounded-lg size-9 bg-n-alpha-2 text-n-slate-11 shrink-0"
      >
        <span class="i-lucide-phone-call size-5" />
      </div>
      <div class="min-w-0 flex-1">
        <div class="font-medium text-n-slate-12">
          {{ t('CONNECTIONS.calls.title') }}
        </div>
        <p v-if="callsBlocked" class="text-sm text-n-amber-11">
          {{
            t('INBOX_MGMT.CALL_METHOD_CONFLICT', {
              active: callConflicts.join(' / '),
              method: 'AstraCall',
            })
          }}
        </p>
        <p v-else class="text-sm text-n-slate-10">
          {{ t('CONNECTIONS.calls.desc') }}
        </p>
      </div>
      <Switch
        :model-value="callsEnabled"
        :disabled="savingCalls || (callsBlocked && !callsEnabled)"
        @update:model-value="toggleCalls"
      />
    </div>

    <!-- Ignorar chamadas recebidas (AstraCall): recusa na hora + msg automática.
         NÃO afeta originar ligação. -->
    <div
      v-if="isAstracall"
      class="p-4 mb-4 border rounded-xl border-n-weak bg-n-solid-1"
    >
      <div class="flex items-center gap-3">
        <div
          class="flex items-center justify-center rounded-lg size-9 bg-n-alpha-2 text-n-slate-11 shrink-0"
        >
          <span class="i-lucide-phone-off size-5" />
        </div>
        <div class="min-w-0 flex-1">
          <div class="font-medium text-n-slate-12">
            {{ t('CONNECTIONS.rejectCalls.title') }}
          </div>
          <p class="text-sm text-n-slate-10">
            {{ t('CONNECTIONS.rejectCalls.desc') }}
          </p>
        </div>
        <Switch
          :model-value="rejectCalls"
          :disabled="savingReject"
          @update:model-value="toggleReject"
        />
      </div>
      <div v-if="rejectCalls" class="mt-3">
        <label class="block mb-1 text-sm font-medium text-n-slate-12">
          {{ t('CONNECTIONS.rejectCalls.messageLabel') }}
        </label>
        <textarea
          v-model="rejectMessage"
          rows="2"
          :placeholder="t('CONNECTIONS.rejectCalls.messagePlaceholder')"
          class="reset-base w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
          @blur="saveRejectMessage"
        />
        <p class="mt-1 text-xs text-n-slate-10">
          {{ t('CONNECTIONS.rejectCalls.messageHint') }}
        </p>
      </div>
    </div>

    <!-- Importar histórico (AstraCall): traz conversas/mensagens do aparelho -->
    <div
      v-if="isAstracall"
      class="p-4 mb-4 border rounded-xl border-n-weak bg-n-solid-1"
    >
      <div class="flex items-center gap-3">
        <div
          class="flex items-center justify-center rounded-lg size-9 bg-n-alpha-2 text-n-slate-11 shrink-0"
        >
          <span class="i-lucide-download size-5" />
        </div>
        <div class="min-w-0 flex-1">
          <div class="font-medium text-n-slate-12">
            {{ t('CONNECTIONS.import.title') }}
          </div>
          <p class="text-sm text-n-slate-10">
            {{ t('CONNECTIONS.import.shortDesc') }}
          </p>
        </div>
        <NextButton
          sm
          blue
          :label="
            importRunning
              ? t('CONNECTIONS.import.running')
              : t('CONNECTIONS.import.start')
          "
          :is-loading="importStarting || importRunning"
          :disabled="importStarting || importRunning"
          @click="startImport"
        />
      </div>

      <!-- período: automático (desde a última msg) OU janela em dias digitada -->
      <div
        class="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 text-sm text-n-slate-11"
      >
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            v-model="importMode"
            type="radio"
            value="auto"
            :disabled="importStarting || importRunning"
          />
          {{ t('CONNECTIONS.import.modeAuto') }}
        </label>
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            v-model="importMode"
            type="radio"
            value="days"
            :disabled="importStarting || importRunning"
          />
          {{ t('CONNECTIONS.import.modeDaysPre') }}
          <input
            v-model.number="importDays"
            type="number"
            min="0"
            class="!h-8 !mb-0 w-20 text-center"
            :disabled="importStarting || importRunning"
            @focus="importMode = 'days'"
          />
          {{ t('CONNECTIONS.import.modeDaysPost') }}
        </label>
      </div>
      <p class="mt-1 text-xs text-n-slate-10">
        {{ t('CONNECTIONS.import.daysHint') }}
      </p>

      <div v-if="importState?.status" class="mt-3">
        <div class="flex items-center justify-between mb-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{
              t(
                `CONNECTIONS.import.state.${importState.status}`,
                importState.status
              )
            }}
          </span>
          <span
            v-if="importState.total_chats"
            class="text-xs tabular-nums text-n-slate-10"
          >
            {{ importState.done_chats || 0 }}/{{ importState.total_chats }}
          </span>
        </div>
        <div class="w-full h-2 overflow-hidden rounded-full bg-n-alpha-2">
          <div
            class="h-full transition-all duration-500 rounded-full bg-n-brand"
            :class="{ 'animate-pulse': importRunning }"
            :style="{ width: importPct + '%' }"
          />
        </div>
        <p
          v-if="importState.imported_messages != null"
          class="mt-2 text-xs text-n-slate-10"
        >
          {{
            t(
              'CONNECTIONS.import.imported',
              importState.imported_messages || 0,
              { n: importState.imported_messages || 0 }
            )
          }}
        </p>
        <p
          v-if="importState.status === 'error' && importState.error"
          class="mt-1 text-xs text-n-ruby-11"
        >
          {{ importState.error }}
        </p>
      </div>

      <!-- Importar contatos: a agenda sincronizada do aparelho na conexão -->
      <div class="pt-4 mt-4 border-t border-n-weak">
        <div class="flex items-center gap-3">
          <div
            class="flex items-center justify-center rounded-lg size-9 bg-n-alpha-2 text-n-slate-11 shrink-0"
          >
            <span class="i-lucide-contact size-5" />
          </div>
          <div class="min-w-0 flex-1">
            <div class="font-medium text-n-slate-12">
              {{ t('CONNECTIONS.importContacts.title') }}
            </div>
            <p class="text-sm text-n-slate-10">
              {{ t('CONNECTIONS.importContacts.desc') }}
            </p>
          </div>
          <NextButton
            sm
            slate
            :label="
              contactsImportRunning
                ? t('CONNECTIONS.importContacts.running')
                : t('CONNECTIONS.importContacts.start')
            "
            :is-loading="contactsImportStarting || contactsImportRunning"
            :disabled="contactsImportStarting || contactsImportRunning"
            @click="startContactsImport"
          />
        </div>
        <p
          v-if="contactsImport?.status === 'running'"
          class="mt-2 text-xs text-n-slate-10"
        >
          {{
            t('CONNECTIONS.importContacts.progress', {
              done: contactsImport.done || 0,
              total: contactsImport.total || 0,
            })
          }}
        </p>
        <p
          v-else-if="contactsImport?.status === 'done'"
          class="mt-2 text-xs text-n-slate-10"
        >
          {{
            t('CONNECTIONS.importContacts.doneMsg', {
              created: contactsImport.created || 0,
              updated: contactsImport.updated || 0,
            })
          }}
        </p>
        <p
          v-else-if="contactsImport?.status === 'error'"
          class="mt-2 text-xs text-n-ruby-11"
        >
          {{ contactsImport.error }}
        </p>
      </div>
    </div>

    <!-- Proxy de saída (AstraCall/UazAPI/Evolution Go): a conexão do WhatsApp sai por ele -->
    <div
      v-if="isAstracall || isUazapi || isEvolutionGo"
      class="p-4 mb-4 border rounded-xl border-n-weak bg-n-solid-1"
    >
      <div class="flex items-center gap-2 mb-1">
        <span class="font-medium text-n-slate-12">
          {{ t('CONNECTIONS.proxy.title') }}
        </span>
        <span
          v-if="proxyEnabled"
          class="px-2 py-0.5 text-[0.6875rem] font-medium rounded-full bg-n-teal-3 text-n-teal-11"
        >
          {{ t('CONNECTIONS.proxy.active') }}
        </span>
      </div>
      <p class="mb-4 text-sm text-n-slate-10">{{ t('CONNECTIONS.proxy.desc') }}</p>

      <div class="flex flex-col gap-3">
        <div class="grid grid-cols-[7rem_1fr_6.5rem] gap-2">
          <select v-model="proxy.scheme" class="!h-9 !mb-0 w-full">
            <option value="socks5">SOCKS5</option>
            <option value="http">HTTP</option>
            <option value="https">HTTPS</option>
          </select>
          <input
            v-model="proxy.host"
            type="text"
            class="w-full !h-9 !mb-0"
            :placeholder="t('CONNECTIONS.proxy.host')"
          />
          <input
            v-model="proxy.port"
            type="text"
            class="w-full !h-9 !mb-0"
            :placeholder="t('CONNECTIONS.proxy.port')"
          />
        </div>
        <div class="grid grid-cols-2 gap-2">
          <input
            v-model="proxy.user"
            type="text"
            class="w-full !h-9 !mb-0"
            :placeholder="t('CONNECTIONS.proxy.user')"
          />
          <input
            v-model="proxy.pass"
            type="password"
            class="w-full !h-9 !mb-0"
            :placeholder="t('CONNECTIONS.proxy.pass')"
          />
        </div>
        <div class="flex items-center gap-2">
          <NextButton
            sm
            blue
            :label="t('CONNECTIONS.proxy.apply')"
            :is-loading="savingProxy"
            :disabled="savingProxy"
            @click="saveProxy"
          />
          <NextButton
            v-if="proxyEnabled"
            sm
            slate
            faded
            :label="t('CONNECTIONS.proxy.remove')"
            :disabled="savingProxy"
            @click="clearProxy"
          />
        </div>
      </div>
    </div>

    <div
      v-if="config"
      class="p-4 border rounded-xl border-n-weak bg-n-solid-1"
    >
      <div class="mb-1 font-medium text-n-slate-12">
        {{ t('CONNECTIONS.config.title') }}
      </div>
      <p class="mb-4 text-sm text-n-slate-10">{{ t('CONNECTIONS.config.desc') }}</p>

      <div class="flex flex-col divide-y divide-n-weak">
        <template v-for="key in TOGGLES" :key="key">
          <div class="flex items-center gap-3 py-3">
            <div class="min-w-0">
              <div class="text-sm font-medium text-n-slate-12">
                {{ t(`CONNECTIONS.config.${key}`) }}
              </div>
              <p class="text-xs text-n-slate-10">
                {{ t(`CONNECTIONS.config.${key}Desc`) }}
              </p>
            </div>
            <Switch
              class="ml-auto"
              :model-value="!!config[key]"
              @update:model-value="val => saveConfig(key, val)"
            />
          </div>

          <div v-if="key === 'rejectCalls' && config.rejectCalls" class="py-3">
            <label class="block mb-1 text-xs font-medium text-n-slate-11">
              {{ t('CONNECTIONS.config.msgCall') }}
            </label>
            <input
              v-model="config.msgCall"
              type="text"
              class="w-full !mb-0 !h-9"
              :placeholder="t('CONNECTIONS.config.msgCallPlaceholder')"
              @change="saveConfig('msgCall', config.msgCall)"
            />
          </div>
        </template>

        <!-- Delay anti-ban (UazAPI): atraso aleatório entre envios (em segundos) -->
        <div v-if="isUazapi" class="py-3">
          <div class="text-sm font-medium text-n-slate-12">
            {{ t('CONNECTIONS.config.msgDelay') }}
          </div>
          <p class="mb-2 text-xs text-n-slate-10">
            {{ t('CONNECTIONS.config.msgDelayDesc') }}
          </p>
          <div class="grid grid-cols-2 gap-2 max-w-xs">
            <div>
              <label class="block mb-1 text-xs text-n-slate-11">
                {{ t('CONNECTIONS.config.msgDelayMin') }}
              </label>
              <input
                v-model.number="config.msgDelayMin"
                type="number"
                min="0"
                class="w-full !mb-0 !h-9"
                @change="saveConfig('msgDelayMin', Number(config.msgDelayMin) || 0)"
              />
            </div>
            <div>
              <label class="block mb-1 text-xs text-n-slate-11">
                {{ t('CONNECTIONS.config.msgDelayMax') }}
              </label>
              <input
                v-model.number="config.msgDelayMax"
                type="number"
                min="0"
                class="w-full !mb-0 !h-9"
                @change="saveConfig('msgDelayMax', Number(config.msgDelayMax) || 0)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <p v-else-if="!loading" class="py-6 text-sm text-n-slate-10">
    {{ t('CONNECTIONS.noInstances') }}
  </p>
</template>
